import { watch } from 'vue'
import { MediaDeviceFailure, RoomEvent } from 'livekit-client'

import type { RemoteParticipant, Room } from 'livekit-client'

import { refRoom } from './room-live-kit.hook'

type Unsubscribe = () => void

export interface NotificationsLiveKitOptions {
  notify: {
    info: (t: string) => void
    warning: (t: string) => string
    success: (t: string) => void
    dismiss: (id: string) => void
  }
  permissions: {
    setDeniedMic: (v: boolean) => void
    setDeniedCam: (v: boolean) => void
    setBlocked: (v: boolean) => void
    isDeniedMic: () => boolean
    isDeniedCam: () => boolean
  }
  analytics: {
    peerJoined: (name: string) => void
    peerLeft: (name: string) => void
    permissionsPopUp: (p: { micStatus: string, camStatus: string }) => void
  }
}

interface UseNotificationsLiveKitReturn {
  listen: () => Unsubscribe
}

export const useNotificationsLiveKit = (options: NotificationsLiveKitOptions): UseNotificationsLiveKitReturn => {
  const nameOf = (participant: RemoteParticipant): string => participant.name || 'A participant'

  let reconnectingToastId: string | null = null

  const dismissReconnecting = (): void => {
    if (!reconnectingToastId) return

    options.notify.dismiss(reconnectingToastId)
    reconnectingToastId = null
  }

  const onConnected = (participant: RemoteParticipant): void => {
    const name = nameOf(participant)
    options.notify.info(`${name} has joined.`)
    options.analytics.peerJoined(name)
  }

  const onDisconnected = (participant: RemoteParticipant): void => {
    const name = nameOf(participant)
    options.notify.info(`${name} has left.`)
    options.analytics.peerLeft(name)
  }

  const onReconnecting = (): void => {
    if (reconnectingToastId) return

    reconnectingToastId = options.notify.warning('Connection lost. Reconnecting…')
  }

  const onReconnected = (): void => {
    dismissReconnecting()
    options.notify.success('Connection restored')
  }

  const onMediaDevicesError = (error: Error, kind?: MediaDeviceKind): void => {
    if (MediaDeviceFailure.getFailure(error) !== MediaDeviceFailure.PermissionDenied) return

    if (kind !== 'audioinput' && kind !== 'videoinput') return

    if (kind === 'audioinput') options.permissions.setDeniedMic(true)
    if (kind === 'videoinput') options.permissions.setDeniedCam(true)

    options.permissions.setBlocked(true)

    options.analytics.permissionsPopUp({
      micStatus: options.permissions.isDeniedMic() ? 'Blocked' : 'Allowed',
      camStatus: options.permissions.isDeniedCam() ? 'Blocked' : 'Allowed',
    })
  }

  const bind = (room: Room | null): void => {
    room
      ?.on(RoomEvent.ParticipantConnected, onConnected)
      .on(RoomEvent.ParticipantDisconnected, onDisconnected)
      .on(RoomEvent.Reconnecting, onReconnecting)
      .on(RoomEvent.Reconnected, onReconnected)
      .on(RoomEvent.MediaDevicesError, onMediaDevicesError)
  }

  const unbind = (room: Room | null): void => {
    room
      ?.off(RoomEvent.ParticipantConnected, onConnected)
      .off(RoomEvent.ParticipantDisconnected, onDisconnected)
      .off(RoomEvent.Reconnecting, onReconnecting)
      .off(RoomEvent.Reconnected, onReconnected)
      .off(RoomEvent.MediaDevicesError, onMediaDevicesError)
  }

  const listen = (): Unsubscribe => {
    const stopWatch = watch(refRoom, (room, previousRoom) => {
      unbind(previousRoom ?? null)
      dismissReconnecting()
      bind(room)
    }, { immediate: true })

    return () => {
      unbind(refRoom.value)
      stopWatch()
      dismissReconnecting()
    }
  }

  return { listen }
}
